import { useState, useEffect } from 'react'
import { FilterRule } from '@/components/ui/advanced-filter'

export interface DomainView {
  id: string
  name: string
  isDefault: boolean
  filters: FilterRule[]
  visibleColumns: string[]
  searchQuery: string
  statusFilter: string
  clientFilter: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  createdAt: Date
  updatedAt: Date
}

const DEFAULT_COLUMNS = [
  'domain', 'status', 'domain_registrar', 'mailbox_provider', 'email_provider',
  'mailboxes', 'emails_sent', 'replies', 'bounces', 'purchase_date',
  'auto_renewal', 'renewal_date', 'expiry_date', 'notes'
]

const DEFAULT_VIEW: DomainView = {
  id: 'default',
  name: 'Default View',
  isDefault: true,
  filters: [],
  visibleColumns: DEFAULT_COLUMNS,
  searchQuery: '',
  statusFilter: 'all',
  clientFilter: 'all',
  createdAt: new Date(),
  updatedAt: new Date()
}

export function useViewSettings() {
  const [views, setViews] = useState<DomainView[]>([DEFAULT_VIEW])
  const [currentViewId, setCurrentViewId] = useState<string>('default')

  // Load views from localStorage on mount
  useEffect(() => {
    const savedViews = localStorage.getItem('domain-views')
    const savedCurrentViewId = localStorage.getItem('domain-current-view-id')
    
    if (savedViews) {
      try {
        const parsedViews = JSON.parse(savedViews).map((view: any) => ({
          ...view,
          createdAt: new Date(view.createdAt),
          updatedAt: new Date(view.updatedAt)
        }))
        setViews(parsedViews)
      } catch (error) {
        console.error('Failed to parse saved views:', error)
      }
    }
    
    if (savedCurrentViewId) {
      setCurrentViewId(savedCurrentViewId)
    }
  }, [])

  // Save views to localStorage when they change
  useEffect(() => {
    localStorage.setItem('domain-views', JSON.stringify(views))
  }, [views])

  // Save current view ID when it changes
  useEffect(() => {
    localStorage.setItem('domain-current-view-id', currentViewId)
  }, [currentViewId])

  const currentView = views.find(view => view.id === currentViewId) || DEFAULT_VIEW

  const createView = (name: string, settings: Partial<DomainView>) => {
    const newView: DomainView = {
      id: `view-${Date.now()}`,
      name,
      isDefault: false,
      filters: [],
      visibleColumns: DEFAULT_COLUMNS,
      searchQuery: '',
      statusFilter: 'all',
      clientFilter: 'all',
      createdAt: new Date(),
      updatedAt: new Date(),
      ...settings
    }
    
    setViews(prev => [...prev, newView])
    setCurrentViewId(newView.id)
    return newView.id
  }

  const updateView = (viewId: string, updates: Partial<DomainView>) => {
    setViews(prev => prev.map(view => 
      view.id === viewId 
        ? { ...view, ...updates, updatedAt: new Date() }
        : view
    ))
  }

  const deleteView = (viewId: string) => {
    if (viewId === 'default') return // Can't delete default view
    
    setViews(prev => prev.filter(view => view.id !== viewId))
    
    // If deleting current view, switch to default
    if (currentViewId === viewId) {
      setCurrentViewId('default')
    }
  }

  const setDefaultView = (viewId: string) => {
    setViews(prev => prev.map(view => ({
      ...view,
      isDefault: view.id === viewId
    })))
  }

  const duplicateView = (viewId: string, newName: string) => {
    const sourceView = views.find(view => view.id === viewId)
    if (!sourceView) return
    
    const newView: DomainView = {
      ...sourceView,
      id: `view-${Date.now()}`,
      name: newName,
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    
    setViews(prev => [...prev, newView])
    return newView.id
  }

  return {
    views,
    currentView,
    currentViewId,
    setCurrentViewId,
    createView,
    updateView,
    deleteView,
    setDefaultView,
    duplicateView
  }
}